/**
 * /ecowitt-to-rachio — SEO landing page that targets the EXACT search query
 * frustrated homeowners type into Google after giving up on the
 * Rachio Community / Facebook threads:
 *
 *   "ecowitt to rachio"
 *   "connect ecowitt sensors to rachio"
 *   "rachio soil moisture sensor"
 *   "ecowitt rachio integration"
 *
 * The page does three jobs in order:
 *   1. Validate the search intent. ("You're not crazy. There's no native way.")
 *   2. Lay out the 4 real paths honestly (Stock Rachio · Rachio's own sensors ·
 *      DIY Home Assistant · TurfPulse).
 *   3. Convert. The TurfPulse path is the only one that's calm, complete, and
 *      doesn't require an Arduino on your dining-room table.
 *
 * Tone: zero hype. We acknowledge Rachio is a great controller and explicitly
 * say we don't replace it. We just bridge the sensor gap.
 *
 * SEO: Article + FAQ JSON-LD, canonical to https://getturfpulse.com/ecowitt-to-rachio,
 * priority 0.9 in sitemap (same as /pricing — high commercial intent).
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { SEO } from '../components/SEO';
import { BreadcrumbJsonLd } from '../components/BreadcrumbJsonLd';
import { FaqJsonLd } from '../components/FaqJsonLd';
import { HowToJsonLd } from '../components/HowToJsonLd';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { InnerCircleCTA } from '../components/InnerCircleCTA';
import { ShareButtons } from '../components/ShareButtons';
import {
  CheckCircle, XCircle, AlertTriangle, ArrowRight, ExternalLink,
  Sparkles, Wrench,
} from 'lucide-react';

const PathCard = ({ tone, label, title, blurb, pros, cons, testid }) => {
  const palette = {
    bad: 'border-rose-300 dark:border-rose-800 bg-rose-50/40 dark:bg-rose-950/20',
    ok: 'border-amber-300 dark:border-amber-800 bg-amber-50/40 dark:bg-amber-950/20',
    hard: 'border-zinc-300 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-900/40',
    good: 'border-emerald-400 dark:border-emerald-600 bg-gradient-to-br from-emerald-50 to-amber-50 dark:from-emerald-950/40 dark:to-amber-950/20 ring-1 ring-emerald-500/20',
  };
  const labelStyle = {
    bad: 'bg-rose-100 dark:bg-rose-900/40 text-rose-800 dark:text-rose-200',
    ok: 'bg-amber-100 dark:bg-amber-900/40 text-amber-900 dark:text-amber-200',
    hard: 'bg-zinc-200 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-200',
    good: 'bg-emerald-600 text-white',
  };
  return (
    <Card className={`${palette[tone]} border-2`} data-testid={testid}>
      <CardContent className="p-5 space-y-4">
        <div className="flex items-center gap-2">
          <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest ${labelStyle[tone]}`}>
            {label}
          </span>
        </div>
        <h3 className="text-xl font-black text-zinc-900 dark:text-zinc-50 leading-tight">{title}</h3>
        <p className="text-sm text-zinc-700 dark:text-zinc-300 leading-relaxed">{blurb}</p>
        <ul className="space-y-1.5 text-sm">
          {pros.map((p, i) => (
            <li key={`p${i}`} className="flex items-start gap-2">
              <CheckCircle className="mt-0.5 w-4 h-4 shrink-0 text-emerald-600 dark:text-emerald-400" />
              <span className="text-zinc-700 dark:text-zinc-200">{p}</span>
            </li>
          ))}
          {cons.map((c, i) => (
            <li key={`c${i}`} className="flex items-start gap-2">
              <XCircle className="mt-0.5 w-4 h-4 shrink-0 text-rose-500 dark:text-rose-400" />
              <span className="text-zinc-700 dark:text-zinc-200">{c}</span>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
};

export default function EcowittToRachioPage() {
  return (
    <div
      className="min-h-screen bg-gradient-to-b from-zinc-50 to-white dark:from-zinc-950 dark:to-zinc-900"
      data-testid="ecowitt-to-rachio-page"
    >
      <SEO
        title="How to connect Ecowitt soil sensors to Rachio — TurfPulse"
        description="Rachio doesn't have native Ecowitt support. Most homeowners give up after wrestling with Home Assistant and IFTTT. Here's an honest comparison of the 4 real paths — including TurfPulse, the bridge that already did the wiring."
        canonical="https://getturfpulse.com/ecowitt-to-rachio"
      />
      <BreadcrumbJsonLd
        items={[
          { label: 'Home', path: '/' },
          { label: 'Ecowitt to Rachio', path: '/ecowitt-to-rachio' },
        ]}
      />
      <FaqJsonLd
        items={[
          {
            q: "Why does Rachio interpret a 50% moisture reading as 75%?",
            a: "Rachio's S1/S2 sensor input expects 'remaining allowable depletion,' not raw soil moisture. In Rachio's model, 100% means 'soil is at field capacity, no depletion yet' and 0% means 'all of MAD has been used up.' So a raw 50% reading from an Ecowitt WH51 is interpreted by Rachio as '50% of allowable depletion is still left,' which maps to roughly 75% effective moisture. This is why DIY Home Assistant bridges that push raw WH51 percentages directly to Rachio see their zones skip watering when they shouldn't. The fix is either (a) write an HA template sensor that converts raw moisture to depletion before pushing to Rachio, or (b) bypass Rachio's S1/S2 input entirely and fire zones via Rachio's REST API — which is what TurfPulse does.",
          },
          {
            q: 'Can I connect Ecowitt soil moisture sensors directly to Rachio?',
            a: "No. As of 2026, Rachio has no native integration with Ecowitt soil moisture sensors. Rachio's controllers support wired soil sensors from a few specific brands (Rain Bird, Hunter, etc.), but Ecowitt's WH51 / WH52 wireless probes don't appear in their compatibility list. The Rachio Community forum has multiple open threads from Ecowitt owners who've confirmed this with Rachio support.",
          },
          {
            q: 'Does Rachio sell its own soil moisture probes?',
            a: "Not as of 2026. Rachio sells the Wireless Flow Meter (a leak-detection device, not a soil probe) and Smart Hose Timer (for hose-end watering, not lawn zones). Their main 'smart' irrigation feature, Weather Intelligence, runs on forecast data — not on what's actually happening in your soil.",
          },
          {
            q: 'How much does the DIY Home Assistant route cost vs TurfPulse?',
            a: "Time, mostly. Home Assistant is free software, but the typical setup involves buying a Raspberry Pi or NUC ($75–$150), installing HA OS, configuring the Ecowitt integration, writing automations, building a Rachio webhook bridge, and maintaining it forever. Plan on 8–20 hours of setup if you've never done it. TurfPulse Inner Circle is $4.99/mo locked for life, with no setup beyond connecting your Rachio API key and pairing zones to probes.",
          },
          {
            q: 'Will TurfPulse replace my Rachio?',
            a: "No. We work WITH your Rachio — we read your Ecowitt probes, apply the agronomic math, and dispatch fires through Rachio's own API. Your existing controller stays right where it is; we're just the brain on top.",
          },
          {
            q: 'Do I need every Ecowitt product to make this work?',
            a: "No. You need the Ecowitt gateway (GW1100 or GW2000, ~$45) and at least one WH51 soil moisture probe (~$30). That's it. You do NOT need the WS90 weather station, the lightning detector, or the leaf-wetness sensor — your local NWS station already covers forecast and ET₀ inputs.",
          },
          {
            q: 'How fast does TurfPulse update from the probe reading?',
            a: "Every 15 minutes. The probes report to Ecowitt's gateway, the gateway pushes to Ecowitt's cloud, and TurfPulse polls that cloud on a 15-min cron. When a fire decision is made, we dispatch to Rachio within seconds.",
          },
          {
            q: "Why doesn't Rachio see my Ecowitt soil sensors?",
            a: "Rachio's controllers only accept input from wired soil sensors plugged into their S1/S2 terminals. Ecowitt's WH51/WH52 probes are wireless and talk only to an Ecowitt gateway over their own RF protocol. The two products were never designed to talk to each other, and neither company has shipped a bridge.",
          },
          {
            q: "Can I connect Ecowitt to Rachio without Home Assistant?",
            a: "Yes. TurfPulse is a hosted service that does the bridge for you — you paste your Rachio API key, pair your Rachio zones to your Ecowitt probes, and TurfPulse runs the soil-aware fire decisions in the cloud. No Raspberry Pi, no YAML, no automation scripts. Setup is roughly 5 minutes versus 8–20 hours for a from-scratch HA build.",
          },
          {
            q: "What soil moisture sensors are Rachio-compatible natively?",
            a: "Rachio's S1/S2 terminals accept normally-open / normally-closed wired sensors from Rain Bird (e.g. SMRT-Y), Hunter (e.g. Soil-Clik), and Toro. They work, but they're wired (require trenching), usually binary (wet/dry rather than continuous percentage), more expensive per zone than an Ecowitt WH51, and don't drive any agronomic models.",
          },
          {
            q: "How do I get true per-zone soil moisture decisions on Rachio?",
            a: "You need (1) one probe per zone (or one per moisture personality — driest + wettest covers most lawns), (2) a software bridge that reads each probe and decides per-zone (Rachio's own S1/S2 gates the entire schedule, not per zone), and (3) an agronomic layer that knows what to do with the readings. TurfPulse handles all three.",
          },
        ]}
      />
      <HowToJsonLd
        name="How to set up an Ecowitt WH51 soil moisture sensor with Rachio"
        description="The simple 5-step path to closed-loop probe-driven irrigation: Ecowitt soil sensors talking to your existing Rachio controller via TurfPulse, no Home Assistant required."
        totalTime="PT30M"
        steps={[
          {
            name: 'Install the Ecowitt gateway',
            text: 'Plug in an Ecowitt GW1100 or GW2000 gateway (~$45 one-time) and add it to your home Wi-Fi using the Ecowitt mobile app.',
          },
          {
            name: 'Insert the WH51 soil probe',
            text: 'Push the WH51 soil moisture probe into the ground about 4 inches deep in your driest zone. The probe pairs to the gateway automatically. ~$30 each.',
          },
          {
            name: 'Get your Rachio API key',
            text: 'Open the Rachio mobile app, go to Account, and copy your free API key.',
          },
          {
            name: 'Make a free TurfPulse account',
            text: 'Sign up at https://getturfpulse.com (no credit card required), paste your Rachio API key and your Ecowitt API key, and pair each Rachio zone to a probe.',
          },
          {
            name: 'Done — closed loop is running',
            text: 'TurfPulse polls your probes every 15 minutes and fires Rachio zones only when the soil agrees. You can verify in the activity feed within an hour.',
          },
        ]}
      />

      {/* Hero — matches search intent verbatim */}
      <section className="border-b border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950">
        <div className="mx-auto max-w-3xl px-6 py-14 sm:py-20">
          <div className="inline-flex items-center gap-2 rounded-full bg-amber-100 dark:bg-amber-900/40 px-3 py-1 text-xs font-bold text-amber-800 dark:text-amber-200">
            <Wrench className="w-3.5 h-3.5" /> The honest answer
          </div>
          <h1 className="mt-4 text-4xl sm:text-5xl font-black tracking-tight text-zinc-900 dark:text-zinc-50 leading-[1.1]">
            How to connect your<br />
            <span className="text-emerald-700 dark:text-emerald-400">Ecowitt soil sensors</span> to your Rachio.
          </h1>
          <p className="mt-5 text-base sm:text-lg text-zinc-700 dark:text-zinc-300 leading-relaxed">
            Short version: <strong>natively, you can't.</strong> Rachio's compatibility list
            includes wired soil sensors from Rain Bird and Hunter — but
            Ecowitt's wireless WH51 / WH52 probes don't have a native path. The{' '}
            <a
              href="https://community.rachio.com/t/ecowitt-soil-moisture-sensors-with-rachio/35056"
              target="_blank"
              rel="noopener noreferrer"
              className="text-emerald-700 dark:text-emerald-400 underline-offset-2 hover:underline inline-flex items-center gap-1"
            >
              Rachio Community thread on this <ExternalLink className="w-3 h-3" />
            </a>{' '}
            has been open since 2024 with no fix from either company.
          </p>
          <p className="mt-3 text-sm text-zinc-600 dark:text-zinc-400">
            This page is the honest comparison of the 4 real paths — including
            TurfPulse, which is the bridge we built because we got tired of
            waiting for one of them to solve it.
          </p>

          {/* Jump-to TOC — helps users find their answer in seconds and
              gives Google a clear page structure for site-link snippets. */}
          <div
            className="mt-7 rounded-lg border border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-900/40 p-4"
            data-testid="ecowitt-to-rachio-toc"
          >
            <div className="text-[10px] uppercase tracking-widest font-bold text-zinc-500 dark:text-zinc-400">
              Jump to your question
            </div>
            <ul className="mt-2 grid gap-1.5 sm:grid-cols-2 text-sm">
              <li><a href="#why-doesnt-rachio-see-ecowitt" className="text-emerald-700 dark:text-emerald-400 hover:underline">Why doesn't Rachio see my Ecowitt sensors?</a></li>
              <li><a href="#rachio-mad-interpretation" className="text-emerald-700 dark:text-emerald-400 hover:underline">Why does Rachio interpret 50% moisture as 75%?</a></li>
              <li><a href="#connect-ecowitt-rachio-without-home-assistant" className="text-emerald-700 dark:text-emerald-400 hover:underline">Can I do this without Home Assistant?</a></li>
              <li><a href="#rachio-soil-moisture-sensor" className="text-emerald-700 dark:text-emerald-400 hover:underline">Does Rachio sell its own soil sensor?</a></li>
              <li><a href="#ecowitt-wh51-with-rachio-setup" className="text-emerald-700 dark:text-emerald-400 hover:underline">5-step Ecowitt WH51 + Rachio setup</a></li>
              <li><a href="#rachio-compatible-soil-sensor" className="text-emerald-700 dark:text-emerald-400 hover:underline">What sensors ARE Rachio-compatible?</a></li>
              <li><a href="#rachio-per-zone-soil-moisture" className="text-emerald-700 dark:text-emerald-400 hover:underline">How to get true per-zone moisture decisions</a></li>
            </ul>
          </div>

          {/* Inline share row — most people who land here from a FB comment
              or Reddit thread know exactly one other person stuck on the
              same problem. Give them the one-click. */}
          <div
            className="mt-6 pt-5 border-t border-zinc-200 dark:border-zinc-800 flex justify-center"
            data-testid="ecowitt-to-rachio-share-top"
          >
            <ShareButtons
              url="https://getturfpulse.com/ecowitt-to-rachio"
              title="How to connect Ecowitt soil sensors to Rachio"
              message="Rachio still has no native Ecowitt support. The Rachio Community thread has been silent for 2 years. Here's the bridge —"
              testIdPrefix="ecowitt-to-rachio-share-top"
            />
          </div>
        </div>
      </section>

      {/* The 4 paths */}
      <section className="mx-auto max-w-6xl px-6 py-14" data-testid="ecowitt-to-rachio-paths">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 rounded-full bg-emerald-100 dark:bg-emerald-900/40 px-3 py-1 text-xs font-bold text-emerald-700 dark:text-emerald-300">
            Four real paths
          </div>
          <h2 className="mt-3 text-2xl sm:text-3xl font-black text-zinc-900 dark:text-zinc-50">
            What are your actual options?
          </h2>
        </div>
        <div className="grid gap-5 lg:grid-cols-4">
          <PathCard
            testid="path-stock"
            tone="bad"
            label="Path A · do nothing"
            title="Stock Rachio + forecast only"
            blurb="Rachio's Weather Intelligence runs on forecast data. It skips a fire when rain is forecast — that's it. No idea what your soil is actually doing."
            pros={['Free (you already own it)']}
            cons={[
              'Watering decisions made on forecast, not soil',
              'No per-zone moisture awareness',
              'Over-waters in shade · under-waters in sun',
            ]}
          />
          <PathCard
            testid="path-rachio-sensors"
            tone="ok"
            label="Path B · their ecosystem"
            title="Buy Rachio-compatible wired sensors"
            blurb="Rain Bird and Hunter wired soil sensors plug directly into Rachio's S1/S2 terminals. They work — they're also more expensive and have fewer features than Ecowitt."
            pros={[
              'Plug-and-play with Rachio',
              'No extra app or cron loop',
            ]}
            cons={[
              'Wired install (trenching or surface-running cable)',
              'Higher per-sensor cost than Ecowitt WH51',
              'Single-channel binary "wet/dry" on most models — no graph',
              "Doesn't unlock TAMU drought-floor or Smith-Kerns models",
            ]}
          />
          <PathCard
            testid="path-diy"
            tone="hard"
            label="Path C · DIY"
            title="Home Assistant / IFTTT bridge"
            blurb="The path most Rachio Community threads end up suggesting. Possible if you're an Arduino person. Not for the rest of us."
            pros={[
              'Full control · open source',
              'Highly customizable',
            ]}
            cons={[
              'Raspberry Pi or NUC required (~$75–$150)',
              '8–20 hours of setup if you have never done it',
              'Brittle: HA breaking-changes can take it offline silently',
              'You are now an unpaid IT department for your sprinklers',
            ]}
          />
          <PathCard
            testid="path-turfpulse"
            tone="good"
            label="Path D · the bridge"
            title="TurfPulse"
            blurb="The brain for your Rachio. Reads your Ecowitt probes every 15 min, applies the agronomic math (TAMU drought-floor, Smith-Kerns dollar-spot, FAO-56 ET₀), dispatches fires through Rachio's API."
            pros={[
              "Plug-and-play: paste your Rachio API key, pair zones to probes, you're done",
              'Per-zone soil-aware fires — never water a zone the soil says is fine',
              'Sensor-gated skip-stack (rain · freeze · wind · MAD · city blackout)',
              'TAMU 12% drought-floor override · Smith-Kerns disease pressure',
              'Activity-feed receipts on every fire and skip',
            ]}
            cons={[
              'Subscription: $4.99/mo locked for life on the Inner Circle founding tier (10 seats)',
            ]}
          />
        </div>
      </section>

      {/* Cost / feature breakdown */}
      <section className="border-y border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950" data-testid="ecowitt-to-rachio-cost-table">
        <div className="mx-auto max-w-4xl px-6 py-14">
          <h2 className="text-2xl sm:text-3xl font-black text-zinc-900 dark:text-zinc-50 text-center mb-3">
            What you'll actually spend
          </h2>
          <p className="text-center text-zinc-600 dark:text-zinc-400 mb-8 text-sm">
            Realistic numbers for a typical 4-zone bermuda lawn. No upsell math, no fake comparisons.
          </p>
          <div className="overflow-hidden rounded-xl border border-zinc-200 dark:border-zinc-800">
            <table className="w-full text-sm">
              <thead className="bg-zinc-100 dark:bg-zinc-900 text-xs uppercase tracking-wider text-zinc-600 dark:text-zinc-300">
                <tr>
                  <th className="px-4 py-3 text-left">Item</th>
                  <th className="px-4 py-3 text-right">Path B (Rachio-compatible wired)</th>
                  <th className="px-4 py-3 text-right bg-emerald-50 dark:bg-emerald-950/30">Path D (Ecowitt + TurfPulse)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-200 dark:divide-zinc-800">
                <tr>
                  <td className="px-4 py-3">Sensors (1 zone)</td>
                  <td className="px-4 py-3 text-right text-zinc-700 dark:text-zinc-300">~$70–$120 (wired soil sensor)</td>
                  <td className="px-4 py-3 text-right font-semibold text-emerald-700 dark:text-emerald-400 bg-emerald-50/40 dark:bg-emerald-950/20">~$30 (Ecowitt WH51)</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">Gateway / hub</td>
                  <td className="px-4 py-3 text-right text-zinc-700 dark:text-zinc-300">Built into Rachio</td>
                  <td className="px-4 py-3 text-right font-semibold text-emerald-700 dark:text-emerald-400 bg-emerald-50/40 dark:bg-emerald-950/20">~$45 (Ecowitt GW1100, one-time)</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">Install</td>
                  <td className="px-4 py-3 text-right text-zinc-700 dark:text-zinc-300">Trench or surface-run cable to controller</td>
                  <td className="px-4 py-3 text-right font-semibold text-emerald-700 dark:text-emerald-400 bg-emerald-50/40 dark:bg-emerald-950/20">Push probe into ground · done</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">Software subscription</td>
                  <td className="px-4 py-3 text-right text-zinc-700 dark:text-zinc-300">None</td>
                  <td className="px-4 py-3 text-right font-semibold text-emerald-700 dark:text-emerald-400 bg-emerald-50/40 dark:bg-emerald-950/20">$4.99/mo (Inner Circle, locked for life)</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 font-semibold">Per-zone moisture graph (24/7)</td>
                  <td className="px-4 py-3 text-right text-zinc-500"><XCircle className="inline w-4 h-4" /> Most are wet/dry binary</td>
                  <td className="px-4 py-3 text-right text-emerald-700 dark:text-emerald-400 bg-emerald-50/40 dark:bg-emerald-950/20"><CheckCircle className="inline w-4 h-4" /> 15-min granularity</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 font-semibold">TAMU drought-floor override</td>
                  <td className="px-4 py-3 text-right text-zinc-500"><XCircle className="inline w-4 h-4" /></td>
                  <td className="px-4 py-3 text-right text-emerald-700 dark:text-emerald-400 bg-emerald-50/40 dark:bg-emerald-950/20"><CheckCircle className="inline w-4 h-4" /></td>
                </tr>
                <tr>
                  <td className="px-4 py-3 font-semibold">Smith-Kerns dollar-spot pressure</td>
                  <td className="px-4 py-3 text-right text-zinc-500"><XCircle className="inline w-4 h-4" /></td>
                  <td className="px-4 py-3 text-right text-emerald-700 dark:text-emerald-400 bg-emerald-50/40 dark:bg-emerald-950/20"><CheckCircle className="inline w-4 h-4" /></td>
                </tr>
                <tr>
                  <td className="px-4 py-3 font-semibold">Application-aware auto-pause (PGR / fert)</td>
                  <td className="px-4 py-3 text-right text-zinc-500"><XCircle className="inline w-4 h-4" /></td>
                  <td className="px-4 py-3 text-right text-emerald-700 dark:text-emerald-400 bg-emerald-50/40 dark:bg-emerald-950/20"><CheckCircle className="inline w-4 h-4" /></td>
                </tr>
                <tr>
                  <td className="px-4 py-3 font-semibold">Activity-feed receipts on every fire</td>
                  <td className="px-4 py-3 text-right text-zinc-500"><XCircle className="inline w-4 h-4" /></td>
                  <td className="px-4 py-3 text-right text-emerald-700 dark:text-emerald-400 bg-emerald-50/40 dark:bg-emerald-950/20"><CheckCircle className="inline w-4 h-4" /></td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="mt-5 text-xs text-zinc-500 dark:text-zinc-500 italic text-center">
            Pricing reflects Amazon list prices for typical 4-zone setups · 2026.
            Wired-sensor pricing varies by brand and dealer.
          </p>
        </div>
      </section>

      {/* ──────────────────────────────────────────────────────────────
          Long-tail SEO sections — each H2 targets a specific search query
          that Home Assistant doesn't dominate. Self-contained sections so
          Google can index each as its own answer; jump-link IDs so the
          page can be deep-linked from social posts and FB threads.
          ────────────────────────────────────────────────────────────── */}
      <section
        className="bg-zinc-50 dark:bg-zinc-900/40 border-y border-zinc-200 dark:border-zinc-800"
        data-testid="ecowitt-to-rachio-questions"
      >
        <div className="mx-auto max-w-3xl px-6 py-14">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-black text-zinc-900 dark:text-zinc-50">
              Common questions, answered straight
            </h2>
            <p className="mt-2 text-sm text-zinc-600 dark:text-zinc-400">
              The questions homeowners actually search before they find this page.
            </p>
          </div>

          <article id="why-doesnt-rachio-see-ecowitt" className="mb-10 scroll-mt-20" data-testid="q-why-doesnt-rachio-see">
            <h3 className="text-xl font-black text-zinc-900 dark:text-zinc-50">
              Why doesn't Rachio see my Ecowitt soil sensors?
            </h3>
            <p className="mt-3 text-zinc-700 dark:text-zinc-200 leading-relaxed">
              Because Rachio's controllers only accept input from <strong>wired</strong> soil
              sensors plugged into their S1 / S2 terminals — and Ecowitt's WH51 / WH52
              probes are <strong>wireless</strong>, talking only to an Ecowitt gateway over
              their own RF protocol. The two products were never designed to talk to each
              other, and neither company has shipped a bridge.
            </p>
            <p className="mt-3 text-zinc-700 dark:text-zinc-200 leading-relaxed">
              Result: even if both are on your home Wi-Fi, your Rachio app won't
              show your soil moisture readings. You're not doing anything wrong.
              The integration just doesn't exist natively.
            </p>
          </article>

          {/* The MAD-interpretation gotcha — added Feb 25 2026 after an r/rachio
              thread surfaced it (CaptainAwesome06). This is the AHA moment most
              homeowners hit AFTER they've already bridged Ecowitt → HA → Rachio
              and discovered their numbers are off. The most-googled phrasing is
              "rachio moisture allowable depletion" / "rachio interprets sensor". */}
          <article id="rachio-mad-interpretation" className="mb-10 scroll-mt-20" data-testid="q-mad-interpretation">
            <h3 className="text-xl font-black text-zinc-900 dark:text-zinc-50">
              Why does Rachio interpret a 50% moisture reading as 75%?
            </h3>
            <p className="mt-3 text-zinc-700 dark:text-zinc-200 leading-relaxed">
              This is the gotcha that breaks every DIY Ecowitt-to-Rachio bridge —
              and almost nobody learns it until they've already wired everything
              up. <strong>Rachio's sensor input expects "remaining allowable depletion,"
              not raw soil moisture.</strong>
            </p>
            <p className="mt-3 text-zinc-700 dark:text-zinc-200 leading-relaxed">
              In Rachio's world, "100%" means <em>no depletion yet — soil is at
              field capacity</em>, and "0%" means <em>all of MAD has been used up
              — time to water</em>. So when you push a raw 50% reading from your
              WH51 into Rachio, it doesn't think "soil is half wet" — it thinks{' '}
              <em>"50% of the allowable depletion is still left,"</em> which
              effectively translates to a soil moisture of around 75%. Your zones
              skip watering when they shouldn't, and your dashboard numbers stop
              matching your probes.
            </p>
            <div className="mt-4 rounded-lg border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30 p-4">
              <div className="text-[10px] uppercase tracking-widest font-bold text-amber-800 dark:text-amber-200">
                The math
              </div>
              <ul className="mt-2 space-y-1 text-sm text-zinc-800 dark:text-zinc-100 font-mono">
                <li>Probe reads <strong>50%</strong> → Rachio thinks 50% of MAD left → effective <strong>~75%</strong></li>
                <li>Probe reads <strong>20%</strong> → Rachio thinks 20% of MAD left → effective <strong>~60%</strong></li>
                <li>Probe reads <strong>0%</strong> → Rachio finally agrees it's time to water</li>
              </ul>
            </div>
            <p className="mt-4 text-zinc-700 dark:text-zinc-200 leading-relaxed">
              <strong>The fix in Home Assistant land</strong> is to write a template
              sensor that converts your raw moisture % into a depletion %, plumb
              THAT into Rachio via the API, and then re-calibrate every time you
              tune your MAD threshold. It works — until it doesn't (after a
              firmware update, a holiday weekend, or a probe swap).
            </p>
            <p className="mt-3 text-zinc-700 dark:text-zinc-200 leading-relaxed">
              <strong>The fix TurfPulse uses</strong> is to bypass Rachio's S1/S2
              sensor input entirely. We read your probes ourselves, compute MAD
              against the FAO-56 reference ET₀ model and your soil-specific
              calibration baseline, and then{' '}
              <strong>fire Rachio zones directly via Rachio's REST API</strong> —
              the same path Rachio's own mobile app uses. No interpretation layer,
              no translation math, no firmware-update surprises. Rachio stays the
              valve controller; TurfPulse is the brain that decides which zone
              runs and for how long.
            </p>
            <p className="mt-3 text-sm">
              <Link
                to="/methodology/probe-driven-irrigation"
                className="text-emerald-700 dark:text-emerald-400 underline font-semibold"
              >
                Read the full probe-driven methodology →
              </Link>
            </p>
          </article>

          <article id="connect-ecowitt-rachio-without-home-assistant" className="mb-10 scroll-mt-20" data-testid="q-without-home-assistant">
            <h3 className="text-xl font-black text-zinc-900 dark:text-zinc-50">
              Can I connect Ecowitt to Rachio <em>without</em> Home Assistant?
            </h3>
            <p className="mt-3 text-zinc-700 dark:text-zinc-200 leading-relaxed">
              Yes. Home Assistant is one path (the DIY/tinkerer path), but it's not
              the only one. TurfPulse is a hosted service that does the bridge for
              you — you paste your Rachio API key, pair your Rachio zones to your
              Ecowitt probes, and we run the soil-aware fire decisions in the cloud.
              No Raspberry Pi, no YAML, no automation scripts.
            </p>
            <p className="mt-3 text-zinc-700 dark:text-zinc-200 leading-relaxed">
              Setup is roughly 5 minutes versus 8–20 hours for a from-scratch HA
              build. Same outcome — your Rachio fires when your soil agrees — with
              the agronomic math (TAMU drought-floor, Smith-Kerns dollar-spot,
              FAO-56 ET₀) added on top.
            </p>
            <p className="mt-3 text-sm">
              <Link
                to="/getting-started"
                className="text-emerald-700 dark:text-emerald-400 underline font-semibold"
              >
                See the 5-minute setup guide →
              </Link>
            </p>
          </article>

          <article id="rachio-soil-moisture-sensor" className="mb-10 scroll-mt-20" data-testid="q-rachio-soil-sensor">
            <h3 className="text-xl font-black text-zinc-900 dark:text-zinc-50">
              Does Rachio sell its own soil moisture sensor?
            </h3>
            <p className="mt-3 text-zinc-700 dark:text-zinc-200 leading-relaxed">
              No, not as a primary in-ground product. Rachio's hardware lineup
              (as of 2026) is the controller itself, the Wireless Flow Meter
              (which is a pipe-leak detector), and the Smart Hose Timer. Their
              "smart" irrigation feature — Weather Intelligence — is forecast-based,
              not soil-based.
            </p>
            <p className="mt-3 text-zinc-700 dark:text-zinc-200 leading-relaxed">
              That's why so many homeowners end up here. Rachio's app gives a
              great forecast-aware experience, but if you want to know what's
              happening 4 inches under your front lawn versus your back lawn,
              you need a separate sensor — and the most-recommended one in
              homeowner forums is the Ecowitt WH51.
            </p>
          </article>

          <article id="ecowitt-wh51-with-rachio-setup" className="mb-10 scroll-mt-20" data-testid="q-wh51-setup">
            <h3 className="text-xl font-black text-zinc-900 dark:text-zinc-50">
              Setting up an Ecowitt WH51 to work with Rachio (the simple way)
            </h3>
            <p className="mt-3 text-zinc-700 dark:text-zinc-200 leading-relaxed">
              The 5-step version, no soldering required:
            </p>
            <ol className="mt-3 space-y-2 list-decimal pl-6 text-zinc-700 dark:text-zinc-200 leading-relaxed">
              <li>
                Install your <strong>Ecowitt GW1100 (or GW2000) gateway</strong>{' '}
                on your home Wi-Fi using the Ecowitt mobile app. ~$45 one-time.
              </li>
              <li>
                Push your <strong>WH51 soil moisture probe</strong> into the
                ground about 4&quot; deep in your driest zone. The probe pairs to
                the gateway automatically. ~$30 each.
              </li>
              <li>
                Get a <strong>Rachio API key</strong> from the Rachio app
                (Account → Get API Key). Free.
              </li>
              <li>
                Make a free <strong>TurfPulse account</strong>, paste your
                Rachio API key, paste your Ecowitt API key (also free, from
                Ecowitt's web dashboard), and pair each Rachio zone to a probe.
              </li>
              <li>
                That's it. TurfPulse polls your probes every 15 minutes and
                fires Rachio zones only when the soil agrees.
              </li>
            </ol>
            <p className="mt-3 text-sm text-zinc-600 dark:text-zinc-400 italic">
              You can get the closed loop running in about 30 minutes start-to-finish, hardware to first fire. No
              network configuration, no port forwarding, no Linux.
            </p>
          </article>

          <article id="rachio-compatible-soil-sensor" className="mb-10 scroll-mt-20" data-testid="q-rachio-compatible">
            <h3 className="text-xl font-black text-zinc-900 dark:text-zinc-50">
              What soil moisture sensors are actually Rachio-compatible?
            </h3>
            <p className="mt-3 text-zinc-700 dark:text-zinc-200 leading-relaxed">
              Rachio's S1 / S2 terminals accept normally-open / normally-closed
              wired sensors from a few brands — typically Rain Bird (e.g. SMRT-Y),
              Hunter (e.g. Soil-Clik), and Toro. These work, but they:
            </p>
            <ul className="mt-3 space-y-1 list-disc pl-6 text-zinc-700 dark:text-zinc-200 leading-relaxed text-sm">
              <li>Are wired (you trench cable from sensor to controller)</li>
              <li>Are usually <strong>binary</strong> (wet / dry) rather than continuous percentage readings</li>
              <li>Cost more per zone than an Ecowitt WH51</li>
              <li>Don't drive any agronomic models — they just gate the next fire</li>
            </ul>
            <p className="mt-3 text-zinc-700 dark:text-zinc-200 leading-relaxed">
              If you want continuous per-zone moisture <em>graphs</em>, agronomic
              modeling (drought-floor, disease pressure), and a setup that
              doesn't involve a trenching shovel, the Ecowitt-via-TurfPulse path
              is what most enthusiasts end up on.
            </p>
          </article>

          <article id="rachio-per-zone-soil-moisture" className="scroll-mt-20" data-testid="q-per-zone">
            <h3 className="text-xl font-black text-zinc-900 dark:text-zinc-50">
              How do I get true per-zone soil moisture decisions on Rachio?
            </h3>
            <p className="mt-3 text-zinc-700 dark:text-zinc-200 leading-relaxed">
              You need three things working together:
            </p>
            <ol className="mt-3 space-y-2 list-decimal pl-6 text-zinc-700 dark:text-zinc-200 leading-relaxed">
              <li>
                <strong>One probe per zone</strong> (or at minimum, one per
                "moisture personality" — a probe in your driest zone and one in
                your wettest covers most lawns).
              </li>
              <li>
                <strong>A bridge that reads each probe and decides per-zone</strong> —
                because Rachio's S1/S2 terminals accept only one sensor and gate
                the entire schedule, not per zone. TurfPulse handles this in
                software: each zone has its own probe binding and its own fire
                decision.
              </li>
              <li>
                <strong>An agronomic layer</strong> that knows what to do with
                the readings. A 22% reading on bermuda fine-sandy-loam means
                something different than 22% on St. Augustine clay. Without the
                model, the number is just a number.
              </li>
            </ol>
            <p className="mt-3 text-zinc-700 dark:text-zinc-200 leading-relaxed">
              TurfPulse is built around exactly this loop. The methodology page
              shows the math (FAO-56, TAMU, Smith-Kerns) and the activity-feed
              shows the receipts on every fire and skip.
            </p>
            <p className="mt-3 text-sm">
              <Link
                to="/methodology/probe-driven-irrigation"
                className="text-emerald-700 dark:text-emerald-400 underline font-semibold"
              >
                Read the methodology →
              </Link>
            </p>
          </article>
        </div>
      </section>
      <section className="mx-auto max-w-3xl px-6 py-14" data-testid="ecowitt-to-rachio-honest">
        <div className="flex items-center gap-2">
          <AlertTriangle className="w-7 h-7 text-amber-600 dark:text-amber-400" />
          <h2 className="text-2xl sm:text-3xl font-black text-zinc-900 dark:text-zinc-50">
            We're not anti-Rachio. We use Rachio.
          </h2>
        </div>
        <p className="mt-4 text-base text-zinc-700 dark:text-zinc-200 leading-relaxed">
          Rachio is one of the best smart controllers on the market. The
          hardware is solid, the API is clean, the app is decent. We use
          Rachio in our own yards and we'd recommend it to a friend.
        </p>
        <p className="mt-3 text-base text-zinc-700 dark:text-zinc-200 leading-relaxed">
          But the truth is: Rachio's "smart" features are <strong>forecast-aware</strong>,
          not <strong>soil-aware</strong>. They skip a fire when rain is forecast.
          They don't know that your front zone has been dry for four days and
          your back zone is still saturated from yesterday's storm. That's the
          gap the Ecowitt probes fill — and that's the gap TurfPulse bridges.
        </p>
        <p className="mt-3 text-base text-zinc-700 dark:text-zinc-200 leading-relaxed">
          If Rachio ever ships a native Ecowitt integration, we'll cheer them on
          and you'll keep using TurfPulse for the agronomic layer (TAMU,
          Smith-Kerns, FAO-56, application-aware pause) that they aren't going
          to build. Until then, we're the bridge.
        </p>
      </section>

      {/* CTA */}
      <section className="border-t border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950" data-testid="ecowitt-to-rachio-cta">
        <div className="mx-auto max-w-3xl px-6 py-14 text-center">
          <div className="inline-flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-emerald-700 dark:text-emerald-400" />
            <h3 className="text-2xl sm:text-3xl font-black text-zinc-900 dark:text-zinc-50">
              Ready to stop wrestling with this?
            </h3>
          </div>
          <p className="mt-3 text-base text-zinc-700 dark:text-zinc-200 max-w-xl mx-auto leading-relaxed">
            Make a free TurfPulse account, paste your Rachio API key, pair your
            zones to your probes, and you're done. The math runs in the
            background. Your Rachio fires when the soil agrees.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Link to="/?auth=1">
              <Button
                className="bg-emerald-700 hover:bg-emerald-800 text-white"
                data-testid="ecowitt-to-rachio-cta-signup"
              >
                Make a free account <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
            <Link to="/getting-started">
              <Button variant="outline" data-testid="ecowitt-to-rachio-cta-start">
                Read the 5-minute starter guide
              </Button>
            </Link>
            <Link to="/methodology/probe-driven-irrigation">
              <Button variant="ghost" data-testid="ecowitt-to-rachio-cta-methodology">
                See the methodology →
              </Button>
            </Link>
          </div>
          <div className="mt-8 flex justify-center" data-testid="ecowitt-to-rachio-inner-circle">
            <InnerCircleCTA testId="ecowitt-to-rachio-inner-circle-cta" size="default" />
          </div>
          <p className="mt-3 text-xs text-zinc-500 dark:text-zinc-400">
            Free tier never asks for a card. Inner Circle: $0 today, $4.99/mo first
            charge Nov 1 2026, locked for life. Cancel anytime.
          </p>

          {/* Landscaper / mow-protection teaser — built reactively after Justin
              reported "I get a lot of inquiries about leaving probes in." This
              is the question every probe-curious homeowner asks; addressing it
              inline here removes the #1 objection before they bounce. */}
          <div
            className="mt-8 rounded-xl border border-zinc-200 dark:border-zinc-800 bg-zinc-50/60 dark:bg-zinc-900/40 p-5"
            data-testid="ecowitt-to-rachio-probe-protection-teaser"
          >
            <div className="text-[10px] uppercase tracking-widest font-bold text-zinc-500 dark:text-zinc-400 mb-1">
              Wait — what about my landscaper?
            </div>
            <p className="text-sm text-zinc-800 dark:text-zinc-200">
              Most common question we get: <em>"Won't a 2-inch mow or a landscape
              crew nick the probes?"</em> Short answer: yes, eventually — if
              you don't protect them. Three real-world fixes (valve box,
              marking flags, strategic placement) with cost breakdowns and a
              Carson box recipe that commercial landscapers actually respect.
            </p>
            <Link to="/protect-your-probes">
              <Button
                size="sm"
                variant="outline"
                className="mt-3"
                data-testid="ecowitt-to-rachio-probe-protection-cta"
              >
                See the protection playbook <ArrowRight className="ml-1 w-3.5 h-3.5" />
              </Button>
            </Link>
          </div>

          {/* Pro audience bridge — irrigation contractors keep landing on this
              page from FB threads. Send them to their own surface instead of
              treating them like homeowners. Added after Tiberias Garrett's
              May 10 thread comment validated the B2B path. */}
          <div
            className="mt-10 pt-6 border-t border-zinc-200 dark:border-zinc-800 rounded-xl bg-amber-50/40 dark:bg-amber-950/20 border border-amber-200/60 dark:border-amber-900/30 p-5"
            data-testid="ecowitt-to-rachio-pro-bridge"
          >
            <div className="text-[10px] uppercase tracking-widest font-bold text-amber-700 dark:text-amber-400 mb-1">
              Reading this as an irrigation pro?
            </div>
            <p className="text-sm text-zinc-800 dark:text-zinc-200">
              <strong>Real contractor feedback</strong> from a Texas irrigation
              pro on this exact topic: <em>"Residential clients are okay with
              overwatering as long as the grass is green — but I do have
              commercial that may take interest."</em> Same instinct we
              built for. TurfPulse stacks on top of your existing Rachio /
              Hydrawise / Rain Bird IQ fleet without touching distribution
              work. First-season pilot is comp'd for verified pros.
            </p>
            <Link to="/for-contractors">
              <Button
                size="sm"
                className="mt-3 bg-amber-500 hover:bg-amber-400 text-zinc-950 font-bold"
                data-testid="ecowitt-to-rachio-pro-bridge-cta"
              >
                See the contractor breakdown <ArrowRight className="ml-1 w-3.5 h-3.5" />
              </Button>
            </Link>
          </div>

          {/* Share row — the Rachio Community thread has been silent for 2 years.
              Every visitor who lands here is a potential distributor. Make it
              one-click for them to push this answer into the next FB / Reddit
              / Rachio Community comment someone leaves. */}
          <div
            className="mt-10 pt-6 border-t border-zinc-200 dark:border-zinc-800"
            data-testid="ecowitt-to-rachio-share-bottom"
          >
            <p className="mb-3 text-sm text-zinc-700 dark:text-zinc-300">
              <strong>Know someone wrestling with this?</strong> The Rachio
              Community thread has been quiet since 2024. Pass it along.
            </p>
            <div className="flex justify-center">
              <ShareButtons
                url="https://getturfpulse.com/ecowitt-to-rachio"
                title="How to connect Ecowitt soil sensors to Rachio"
                message="Rachio still has no native Ecowitt support. The Rachio Community thread has been silent for 2 years. TurfPulse is the bridge — 5 min, no Raspberry Pi."
                testIdPrefix="ecowitt-to-rachio-share-bottom"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
